// src/components/Profile.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import aioticslogo from "../assets/aiotics.png";
import "../styles/Profile.css";

let interceptorAdded = false;

const Profile = ({ onLogout }) => {
  const [activePage, setActivePage] = useState("profile");
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
  });
  const [updateMsg, setUpdateMsg] = useState("");

  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    role: "viewuser",
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createMsg, setCreateMsg] = useState("");

  const [allUsers, setAllUsers] = useState([]);
  const [roleUpdateMsg, setRoleUpdateMsg] = useState("");

  const [timeLeft, setTimeLeft] = useState(null);
  const [inactivityTimeoutMinutes, setInactivityTimeoutMinutes] = useState(null);

  const [sessionTimeout, setSessionTimeout] = useState(null);
  const [timeoutUpdateMsg, setTimeoutUpdateMsg] = useState("");

  const [showBothTimers, setShowBothTimers] = useState(() => {
    return localStorage.getItem("showBothTimers") === "true";
  });

  // LDAP config state
  const [ldapConfig, setLdapConfig] = useState({
    ldap_url: "",
    ldap_base_dn: "",
    ldap_user_dn_field: "",
    ldap_bind_user: "",
    ldap_bind_password: ""
  });
  const [ldapMsg, setLdapMsg] = useState("");
  const [showBindPassword, setShowBindPassword] = useState(false);
  const [ldapSaving, setLdapSaving] = useState(false);
  const [ldapTestResult, setLdapTestResult] = useState("");

  const menuItems = [
    { key: "profile", label: "My Profile" },
    { key: "createUser", label: "Create User", adminOnly: true },
    { key: "manageUsers", label: "Manage Users", adminOnly: true },
    { key: "sessionTimeout", label: "Session Timeout", adminOnly: true },
    { key: "ldapConfig", label: "LDAP Config", adminOnly: true }
  ];

  // Persist toggle
  useEffect(() => {
    localStorage.setItem("showBothTimers", String(showBothTimers));
  }, [showBothTimers]);

  // Axios interceptor for session expiry
  useEffect(() => {
    if (interceptorAdded) return;

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const msg = error.response.data?.message || "";
          if (
            msg.includes("token") ||
            msg.includes("Session expired") ||
            msg.includes("Invalid session") ||
            msg.includes("logged in elsewhere")
          ) {
            localStorage.removeItem("token");
            if (onLogout) onLogout();
            window.location.href = "/";
          }
        }
        return Promise.reject(error);
      }
    );

    interceptorAdded = true;
    return () => {
      axios.interceptors.response.eject(interceptor);
      interceptorAdded = false;
    };
  }, [onLogout]);

  // JWT timer
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("⚠️ No token found. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiryTime = payload.exp * 1000;
      const updateTimer = () => {
        const remaining = expiryTime - Date.now();
        setTimeLeft(remaining > 0 ? remaining : 0);
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } catch (e) {
      console.error("Failed to decode token", e);
      setError("⚠️ Invalid token. Please login again.");
      setLoading(false);
    }
  }, []);

  // Fetch profile + data
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    axios
      .get("/api/profile", { headers: { Authorization: `Bearer ${token}` } })
      .then((profileRes) => {
        const user = profileRes.data;
        setUserDetails(user);
        setFormData({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          mobile: user.mobile || "",
        });

        return axios.get("/api/session-timeout", {
          headers: { Authorization: `Bearer ${token}` }
        }).then(timeoutRes => {
          setInactivityTimeoutMinutes(timeoutRes.data.sessionTimeout);
        }).catch(err => {
          console.warn("Could not fetch session timeout (non-critical)", err);
        });
      })
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        console.error("Profile fetch failed", err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("token");
          setError("⚠️ Session expired. Please login again.");
        } else {
          setError("⚠️ Failed to load profile.");
        }
        setLoading(false);
      });

    if (userDetails?.role === "admin") {
      const token = localStorage.getItem("token");
      axios
        .get("/api/all-users", { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setAllUsers(res.data))
        .catch(console.error);

      axios
        .get("/api/session-timeout", { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setSessionTimeout(res.data.sessionTimeout))
        .catch(console.error);

      axios
        .get("/api/ldap/config", { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          const cfg = r.data || {};
          setLdapConfig({
            ldap_url: cfg.ldap_url || "",
            ldap_base_dn: cfg.ldap_base_dn || "",
            ldap_user_dn_field: cfg.ldap_user_dn_field || "",
            ldap_bind_user: cfg.ldap_bind_user || "",
            ldap_bind_password: ""
          });
        })
        .catch((e) => {
          console.warn("Could not fetch LDAP config (may be empty)", e);
        });
    }
  }, [userDetails?.role]);

  // Handlers
  const handleLogout = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        await axios.post("/api/logout", {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch {}
    }
    localStorage.removeItem("token");
    if (onLogout) onLogout();
    window.location.href = "/";
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      await axios.put("/api/update-profile", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserDetails({ ...userDetails, ...formData });
      setUpdateMsg("✅ Profile updated successfully");
      setEditMode(false);
    } catch {
      setUpdateMsg("❌ Failed to update profile");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateMsg("");
    const token = localStorage.getItem("token");
    try {
      const res = await axios.post("/api/create-user", newUser, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreateMsg(`✅ User created successfully (ID: ${res.data.userId})`);
      setNewUser({
        username: "",
        password: "",
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        role: "viewuser",
      });
      setShowCreateForm(false);

      const usersRes = await axios.get("/api/all-users", { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(usersRes.data);
    } catch (err) {
      setCreateMsg(err.response?.data?.message || "❌ Failed to create user");
    }
  };

  const handleRoleChange = async (id, newRole) => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(`/api/update-user-role/${id}`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoleUpdateMsg("✅ Role updated successfully");
      setAllUsers(allUsers.map(u => u.id === id ? { ...u, role: newRole } : u));
    } catch {
      setRoleUpdateMsg("❌ Failed to update role");
    }
  };

  const handleUpdateSessionTimeout = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      const response = await axios.put("/api/session-timeout", { minutes: Number(sessionTimeout) }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTimeoutUpdateMsg("✅ Session timeout updated successfully");
      setInactivityTimeoutMinutes(response.data.sessionTimeout);
    } catch (err) {
      console.error(err.response?.data);
      setTimeoutUpdateMsg("❌ Failed to update session timeout");
    }
  };

  // LDAP save with enhanced error handling
  const handleSaveLdapConfig = async (e) => {
    e.preventDefault();
    setLdapSaving(true);
    setLdapMsg("");
    setLdapTestResult("");
    const token = localStorage.getItem("token");
    try {
      const payload = {
        ldap_url: ldapConfig.ldap_url,
        ldap_base_dn: ldapConfig.ldap_base_dn,
        ldap_user_dn_field: ldapConfig.ldap_user_dn_field,
        ldap_bind_user: ldapConfig.ldap_bind_user,
        ldap_bind_password: ldapConfig.ldap_bind_password || null
      };
      await axios.post("/api/ldap/config", payload, { headers: { Authorization: `Bearer ${token}` } });
      setLdapMsg("✅ LDAP configuration saved successfully");
      setLdapConfig(prev => ({ ...prev, ldap_bind_password: "" }));
    } catch (err) {
      console.error("LDAP save failed", err.response?.data || err);
      const errorMsg = err.response?.data?.message || "Failed to save LDAP configuration";
      if (errorMsg.includes("bind failed")) {
        setLdapMsg("❌ LDAP bind failed - please check your credentials");
      } else if (errorMsg.includes("not configured") || errorMsg.includes("required")) {
        setLdapMsg("❌ Invalid LDAP configuration - please check all required fields");
      } else if (errorMsg.includes("search error")) {
        setLdapMsg("❌ LDAP search failed - please verify Base DN and User DN Field");
      } else {
        setLdapMsg(`❌ ${errorMsg}`);
      }
    } finally {
      setLdapSaving(false);
    }
  };
  const [ldapTesting, setLdapTesting] = useState(false);
  // Test LDAP connection
const testLdapConnection = async () => {
  setLdapTesting(true);
  setLdapTestResult("");
  const token = localStorage.getItem("token");
  try {
    const testPayload = {
      ldap_url: ldapConfig.ldap_url,
      ldap_base_dn: ldapConfig.ldap_base_dn,
      ldap_user_dn_field: ldapConfig.ldap_user_dn_field,
      ldap_bind_user: ldapConfig.ldap_bind_user,
      ldap_bind_password: ldapConfig.ldap_bind_password || null
    };
    
    await axios.post("/api/ldap/test", testPayload, { 
      headers: { Authorization: `Bearer ${token}` } 
    });
    setLdapTestResult("✅ LDAP connection test passed");
  } catch (err) {
    console.error("LDAP test failed", err);
    const errorMsg = err.response?.data?.message || "LDAP connection test failed";
    setLdapTestResult(`❌ ${errorMsg}`);
  } finally {
    setLdapTesting(false);
  }
};

  const formatTime = (ms) => {
    if (ms === null) return "";
    if (ms <= 0) return "Expired";
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}m ${sec}s`;
  };

  if (loading) return <div className="profile-wrapper"><p>Loading...</p></div>;
  if (error) return <div className="profile-wrapper"><p>{error}</p><button onClick={handleLogout}>Go to Login</button></div>;

  const visibleMenuItems = menuItems.filter(
    item => !item.adminOnly || userDetails?.role === "admin"
  );


  return (
    <div className="profile-wrapper" style={{ display: "flex", gap: "0" }}>
      {/* Sidebar */}
      <div style={{ width: "220px", background: "rgba(255,255,255,0.95)", padding: "20px", borderRadius: "10px 0 0 10px" }}>
        {visibleMenuItems.map(item => (
          <div
            key={item.key}
            onClick={() => setActivePage(item.key)}
            style={{
              padding: "12px",
              marginBottom: "6px",
              cursor: "pointer",
              fontWeight: activePage === item.key ? "700" : "500",
              background: activePage === item.key ? "#007bff" : "transparent",
              color: activePage === item.key ? "#fff" : "#333",
              borderRadius: "6px"
            }}
          >
            {item.label}
          </div>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: "20px" }}>
        {activePage === "profile" && (
          <div className="profile-card">
            {!editMode ? (
              <>
                <div className="profile-grid">
                  <div className="profile-item"><span className="label">Username:</span> <span className="value">{userDetails.username}</span></div>
                  <div className="profile-item"><span className="label">Role:</span> <span className="value">{userDetails.role}</span></div>
                  <div className="profile-item"><span className="label">First Name:</span> <span className="value">{userDetails.firstName || "-"}</span></div>
                  <div className="profile-item"><span className="label">Last Name:</span> <span className="value">{userDetails.lastName || "-"}</span></div>
                  <div className="profile-item"><span className="label">Email:</span> <span className="value">{userDetails.email || "-"}</span></div>
                  <div className="profile-item"><span className="label">Mobile:</span> <span className="value">{userDetails.mobile || "-"}</span></div>
                </div>
              </>
            ) : (
              <form onSubmit={handleUpdateProfile} className="update-profile-form">
                <input type="text" placeholder="First Name" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} />
                <input type="text" placeholder="Last Name" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} />
                <input type="email" placeholder="Email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                <input type="text" placeholder="Mobile" value={formData.mobile} onChange={e => setFormData({...formData, mobile: e.target.value})} />
                <button type="submit">Save</button>
                <button type="button" onClick={()=>setEditMode(false)}>Cancel</button>
              </form>
            )}
            {updateMsg && <p className="message">{updateMsg}</p>}
            {!editMode && (
              <div className="button-group">
                <button className="edit-btn" onClick={()=>setEditMode(true)}>✏️ Edit Profile</button>
                <button className="logout-btn" onClick={handleLogout}>Logout</button>
              </div>
            )}

            {userDetails.role === "admin" && inactivityTimeoutMinutes !== null && (
              <div style={{ marginTop: "12px", marginBottom: "12px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={showBothTimers}
                    onChange={(e) => setShowBothTimers(e.target.checked)}
                  />
                  Show JWT expiry timer to all users
                </label>
              </div>
            )}

            {inactivityTimeoutMinutes !== null && (
              <p className="session-timer">
                ⏱️ Inactivity timeout: <strong>{inactivityTimeoutMinutes} minutes</strong>
                <span style={{ fontSize: "0.85em", color: "#666", marginLeft: "6px" }}>
                  (logout if idle)
                </span>
              </p>
            )}

            {showBothTimers && timeLeft !== null && (
              <p className="session-timer">
                ⏳ JWT expires in <strong>{formatTime(timeLeft)}</strong>
                <span style={{ fontSize: "0.85em", color: "#666", marginLeft: "6px" }}>
                  (fixed at login)
                </span>
              </p>
            )}
          </div>
        )}

        {activePage === "createUser" && userDetails.role === "admin" && (
          <div className="profile-card">
            <button onClick={()=>setShowCreateForm(!showCreateForm)}>{showCreateForm ? "➖ Cancel" : "➕ Show Form"}</button>
            {showCreateForm && (
              <form onSubmit={handleCreateUser} className="create-user-form">
                <input placeholder="Username" value={newUser.username} onChange={e=>setNewUser({...newUser, username:e.target.value})} required/>
                <input placeholder="Password" type="password" value={newUser.password} onChange={e=>setNewUser({...newUser, password:e.target.value})} required/>
                <input placeholder="First Name" value={newUser.firstName} onChange={e=>setNewUser({...newUser, firstName:e.target.value})}/>
                <input placeholder="Last Name" value={newUser.lastName} onChange={e=>setNewUser({...newUser, lastName:e.target.value})}/>
                <input placeholder="Email" value={newUser.email} onChange={e=>setNewUser({...newUser, email:e.target.value})}/>
                <input placeholder="Mobile" value={newUser.mobile} onChange={e=>setNewUser({...newUser, mobile:e.target.value})}/>
                <select value={newUser.role} onChange={e=>setNewUser({...newUser, role:e.target.value})}>
                  <option value="admin">Admin</option>
                  <option value="operator">Operator (All Access)</option>
                  <option value="operator_Pharma">Operator - Pharma</option>
                  <option value="operator_Mfg">Operator - Manufacturing</option>
                  <option value="operator_Powerplant">Operator - Powerplant</option>
                  <option value="viewuser">View Only User</option>
                </select>
                <button type="submit">Create User</button>
              </form>
            )}
            {createMsg && <p className="message">{createMsg}</p>}
          </div>
        )}

        {activePage === "manageUsers" && userDetails.role === "admin" && (
          <div className="manage-users">
            {roleUpdateMsg && <p className="message">{roleUpdateMsg}</p>}
            <div className="users-table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Role</th>
                    <th>Update Role</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map(user => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.firstName || "-"}</td>
                      <td>{user.lastName || "-"}</td>
                      <td>{user.email || "-"}</td>
                      <td>{user.mobile || "-"}</td>
                      <td>{user.role}</td>
                      <td>
                        <select value={user.role} onChange={e => handleRoleChange(user.id, e.target.value)}>
                          <option value="admin">Admin</option>
                          <option value="operator">Operator (All Access)</option>
                          <option value="operator_Pharma">Operator - Pharma</option>
                          <option value="operator_Mfg">Operator - Manufacturing</option>
                          <option value="operator_Powerplant">Operator - Powerplant</option>
                          <option value="viewuser">View Only User</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activePage === "sessionTimeout" && userDetails.role === "admin" && (
          <div className="profile-card">
            <h3>Session Timeout (minutes)</h3>
            <form onSubmit={handleUpdateSessionTimeout} className="update-profile-form">
              <input
                type="number"
                min="1"
                value={sessionTimeout || ""}
                onChange={(e) => setSessionTimeout(e.target.value)}
              />
              <button type="submit">Update Timeout</button>
            </form>
            {timeoutUpdateMsg && <p className="message">{timeoutUpdateMsg}</p>}
          </div>
        )}

        {/* LDAP Config tab with enhancements */}
        {activePage === "ldapConfig" && userDetails.role === "admin" && (
          <div className="profile-card">
            <h3>LDAP Configuration</h3>
            <form onSubmit={handleSaveLdapConfig} className="update-profile-form" style={{ maxWidth: 560 }}>
              <label>LDAP URL (e.g. ldap://10.0.0.5:389)</label>
              <input
                placeholder="ldap://ldap.example.com:389"
                value={ldapConfig.ldap_url}
                onChange={e => setLdapConfig({...ldapConfig, ldap_url: e.target.value})}
                required
              />
              <label>Base DN (e.g. dc=example,dc=com)</label>
              <input
                placeholder="dc=example,dc=com"
                value={ldapConfig.ldap_base_dn}
                onChange={e => setLdapConfig({...ldapConfig, ldap_base_dn: e.target.value})}
                required
              />
              <label>User DN Field (e.g. uid or sAMAccountName)</label>
              <input
                placeholder="uid"
                value={ldapConfig.ldap_user_dn_field}
                onChange={e => setLdapConfig({...ldapConfig, ldap_user_dn_field: e.target.value})}
                required
              />
              <label>Bind User (optional)</label>
              <input
                placeholder="cn=admin,dc=example,dc=com"
                value={ldapConfig.ldap_bind_user}
                onChange={e => setLdapConfig({...ldapConfig, ldap_bind_user: e.target.value})}
              />
              <label>Bind Password (optional)</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type={showBindPassword ? "text" : "password"}
                  placeholder="Bind user password (optional)"
                  value={ldapConfig.ldap_bind_password}
                  onChange={e => setLdapConfig({...ldapConfig, ldap_bind_password: e.target.value})}
                />
               <div className="profile-main-content">
                <button
                    type="button"
                    onClick={() => setShowBindPassword((s) => !s)}
                    className={`password-toggle ${showBindPassword ? "hide" : "show"}`}
                ></button>
                </div>

              </div>

              <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
                <button type="submit" disabled={ldapSaving} style={{ minWidth: 160 }}>
                  {ldapSaving ? "Saving..." : "Save LDAP Config"}
                </button>
                    <button 
                    type="button" 
                    onClick={testLdapConnection}
                    disabled={ldapTesting}
                    style={{ 
                        background: "#6c757d", 
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        minWidth: 140
                    }}
                    >
                    {ldapTesting ? "Testing..." : "Test Connection"}
                    </button>
              </div>
            </form>
            {ldapMsg && <p className="message">{ldapMsg}</p>}
            {ldapTestResult && <p className="message">{ldapTestResult}</p>}
            <p style={{ marginTop: 12, fontSize: 13, color: "#666" }}>
              Note: LDAP credentials are stored securely in the database. Bind password is encrypted and only accessible to admins.
            </p>
          </div>
        )}

        <img src={aioticslogo} alt="Aiotics Logo" className="bottom-logo" />
      </div>
    </div>
  );
};

export default Profile;